import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { useAuth, useBookings } from '../../../store/hooks';
import { 
  createBookingStep1, 
  updateBookingStep2, 
  updateBookingStep3, 
  updateBookingStep4,
  setCurrentStep,
  resetBookingFlow
} from '../../../store/slices/bookingSlice';
import { showLoading, updateToast } from '../../../utils/toastr';
import { 
  User, 
  MapPin, 
  Package, 
  Calendar, 
  Camera, 
  CheckCircle, 
  ArrowRight, 
  ArrowLeft,
  Clock,
  Phone,
  Mail,
  FileText
} from 'lucide-react';

const BookAppointment = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { user } = useAuth();
  const { loading, currentStep, bookingId, dispatch } = useBookings();

  const CITY_PINCODE_MAP = {
    Kalka: "133302",
    Pinjore: "134102",
    Panchkula: "134109"
  };

  const [bookingData, setBookingData] = useState({
    name: '',
    email: '',
    mobile: '',
    address: '',
    city: '',
    pincode: '',
    landmark: '',
    scrapType: '',
    quantity: '',
    preferredDate: '',
    preferredTime: '',
    photos: [],
    additionalNotes: ''
  });

  useEffect(() => {
    // Load user data from Redux store
    if (user) {
      setBookingData(prev => ({
        ...prev,
        name: user.name || '',
        email: user.email || '',
        mobile: user.mobile || ''
      }));
    }

    // Load prefilled date and time from URL parameters
    const prefilledDate = searchParams.get('preferredDate');
    const prefilledTime = searchParams.get('preferredTime');

    if (prefilledDate || prefilledTime) {
      setBookingData(prev => ({
        ...prev,
        ...(prefilledDate && { preferredDate: prefilledDate }),
        ...(prefilledTime && { preferredTime: prefilledTime })
      }));
    }
  }, [user, searchParams]);

  // Cleanup effect to reset booking flow when component unmounts
  useEffect(() => {
    return () => {
      dispatch(resetBookingFlow());
    };
  }, [dispatch]);

  const handleInputChange = (e) => {
    const { name, value } = e.target;

    // ✅ Mobile validation
    if (name === 'mobile') {
      const numericValue = value.replace(/\D/g, '').slice(0, 10);
      setBookingData(prev => ({
        ...prev,
        mobile: numericValue
      }));
      return;
    }

    // ✅ Auto pincode when city selected
    if (name === 'city') {
      const pincode = CITY_PINCODE_MAP[value] || "";

      setBookingData(prev => ({
        ...prev,
        city: value,
        pincode: pincode
      }));
      return;
    }

    // ✅ Normal fields
    setBookingData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handlePhotoUpload = (e) => {
    const files = Array.from(e.target.files);
    const newPhotos = files.map(file => ({
      file,
      preview: URL.createObjectURL(file),
      id: Date.now() + Math.random()
    }));

    setBookingData(prev => ({
      ...prev,
      photos: [...prev.photos, ...newPhotos]
    }));
  };

  const removePhoto = (photoId) => {
    setBookingData(prev => ({
      ...prev,
      photos: prev.photos.filter(photo => photo.id !== photoId)
    }));
  };

  const handleNext = async () => {
    const loadingToast = showLoading('Processing...');

    try {
      if (currentStep === 1) {
        // Step 1: Create booking with name and mobile
        const emailValid = /[^\s@]+@[^\s@]+\.[^\s@]+/.test(bookingData.email);

        if (!bookingData.name || !bookingData.mobile || !bookingData.email || !emailValid) {
          updateToast(
            loadingToast,
            'Please enter name, valid email and 10-digit mobile',
            'error'
          );
          return;
        }

        const step1Data = {
          name: bookingData.name,
          email: bookingData.email,
          mobile: bookingData.mobile
        };

        await dispatch(createBookingStep1(step1Data)).unwrap();
        updateToast(
          loadingToast,
          'Step 1 completed successfully!',
          'success'
        );

      } else if (currentStep === 2) {
        // Step 2: Update address details
        if (!bookingData.address || !bookingData.city || !bookingData.pincode) {
          updateToast(
            loadingToast,
            'Please fill in all required address fields',
            'error'
          );
          return;
        }

        const step2Data = {
          name: bookingData.name,
          email: bookingData.email,
          mobile: bookingData.mobile,
          address: bookingData.address,
          city: bookingData.city,
          pincode: bookingData.pincode,
          landmark: bookingData.landmark
        };

        await dispatch(
          updateBookingStep2({ id: bookingId, step2Data })
        ).unwrap();

        updateToast(
          loadingToast,
          'Address details saved successfully!',
          'success'
        );

      } else if (currentStep === 3) {
        // Step 3: Update scrap details
        if (!bookingData.scrapType || !bookingData.quantity) {
          updateToast(
            loadingToast,
            'Please select scrap type and enter quantity',
            'error'
          );
          return;
        }

        const step3Data = {
          name: bookingData.name,
          email: bookingData.email,
          mobile: bookingData.mobile,
          address: bookingData.address,
          city: bookingData.city,
          pincode: bookingData.pincode,
          landmark: bookingData.landmark,
          scrapType: bookingData.scrapType,
          quantity: bookingData.quantity,
          // photoUrl: bookingData.photos.length > 0 ? bookingData.photos[0].preview : null
          photo: bookingData.photos.length > 0
            ? bookingData.photos[0].file
            : null
        };

        await dispatch(
          updateBookingStep3({ id: bookingId, step3Data })
        ).unwrap();

        updateToast(
          loadingToast,
          'Scrap details saved successfully!',
          'success'
        );

      } else if (currentStep === 4) {
        // Step 4: Final booking details
        if (!bookingData.preferredDate || !bookingData.preferredTime) {
          updateToast(
            loadingToast,
            'Please select preferred date and time',
            'error'
          );
          return;
        }

        const step4Data = {
          name: bookingData.name,
          email: bookingData.email,
          mobile: bookingData.mobile,
          address: bookingData.address,
          city: bookingData.city,
          pincode: bookingData.pincode,
          landmark: bookingData.landmark,
          scrapType: bookingData.scrapType,
          quantity: bookingData.quantity,
          photoUrl: bookingData.photos.length > 0
            ? bookingData.photos[0].preview
            : null,
          notes: bookingData.additionalNotes,
          pickupDate: bookingData.preferredDate,
          pickupSlot: bookingData.preferredTime
        };

        const result = await dispatch(
          updateBookingStep4({ id: bookingId, step4Data })
        ).unwrap();

        updateToast(
          loadingToast,
          'Booking completed successfully!',
          'success'
        );

        // Navigate to success page
        setTimeout(() => {
          navigate('/booking-success', {
            state: {
              bookingId: result.bookingId || result.id,
              bookingData: result
            }
          });
        }, 1500);
      }
    } catch (error) {
      updateToast(
        loadingToast,
        error || 'Something went wrong. Please try again.',
        'error'
      );
    }
  };

  const handlePrevious = () => {
    if (currentStep > 1) {
      dispatch(setCurrentStep(currentStep - 1));
    }
  };

  // Reset booking flow when component unmounts
  useEffect(() => {
    return () => {
      dispatch(resetBookingFlow());
    };
  }, [dispatch]);

  const renderStep1 = () => (
    <motion.div
      initial={{ opacity: 0, x: 20 }}
      animate={{ opacity: 1, x: 0 }}
      exit={{ opacity: 0, x: -20 }}
      transition={{ duration: 0.3 }}
      className="space-y-6"
    >
      <div className="text-center mb-8">
        <div className="w-16 h-16 bg-gradient-to-r from-[#102018] to-[#2F6F45] rounded-2xl flex items-center justify-center mx-auto mb-4 shadow-lg">
          <User className="w-8 h-8 text-white" />
        </div>

        <h2 className="text-2xl font-heading font-bold text-[#102018] mb-2">
          Personal Information
        </h2>

        <p className="text-[#718078]">
          Let's start with your basic details
        </p>
      </div>

      <div className="space-y-6">
        <div>
          <label
            htmlFor="name"
            className="block text-sm font-medium text-[#102018] mb-2"
          >
            Full Name *
          </label>

          <input
            type="text"
            id="name"
            name="name"
            value={bookingData.name}
            onChange={handleInputChange}
            placeholder="Enter your full name"
            className="w-full px-4 py-3 border border-[#DDE8DF] rounded-xl focus:ring-2 focus:ring-[#70BD7D] focus:border-[#2F6F45] transition-all duration-200 bg-white text-[#102018]"
            required
          />
        </div>

        <div>
          <label
            htmlFor="email"
            className="block text-sm font-medium text-[#102018] mb-2"
          >
            Email Address *
          </label>

          <input
            type="email"
            id="email"
            name="email"
            value={bookingData.email}
            onChange={handleInputChange}
            placeholder="Enter your email address"
            className="w-full px-4 py-3 border border-[#DDE8DF] rounded-xl focus:ring-2 focus:ring-[#70BD7D] focus:border-[#2F6F45] transition-all duration-200 bg-white text-[#102018]"
            required
          />
        </div>

        <div>
          <label
            htmlFor="mobile"
            className="block text-sm font-medium text-[#102018] mb-2"
          >
            Mobile Number *
          </label>

          <input
            type="tel"
            id="mobile"
            name="mobile"
            value={bookingData.mobile}
            onChange={handleInputChange}
            placeholder="Enter your 10-digit mobile number"
            className="w-full px-4 py-3 border border-[#DDE8DF] rounded-xl focus:ring-2 focus:ring-[#70BD7D] focus:border-[#2F6F45] transition-all duration-200 bg-white text-[#102018]"
            required
            minLength="10"
            maxLength="10"
            pattern="[0-9]{10}"
          />

          {bookingData.mobile.length > 0 &&
            bookingData.mobile.length < 10 && (
              <p className="text-sm text-red-500 mt-1">
                Please enter exactly 10 digits ({bookingData.mobile.length}/10)
              </p>
            )}
        </div>
      </div>
    </motion.div>
  );

  const renderStep2 = () => (
    <motion.div
      initial={{ opacity: 0, x: 20 }}
      animate={{ opacity: 1, x: 0 }}
      exit={{ opacity: 0, x: -20 }}
      transition={{ duration: 0.3 }}
      className="space-y-6"
    >
      <div className="text-center mb-8">
        <div className="w-16 h-16 bg-gradient-to-r from-[#2F6F45] to-[#70BD7D] rounded-2xl flex items-center justify-center mx-auto mb-4 shadow-lg">
          <MapPin className="w-8 h-8 text-white" />
        </div>

        <h2 className="text-2xl font-heading font-bold text-[#102018] mb-2">
          Address Details
        </h2>

        <p className="text-[#718078]">
          Where should we pick up your scrap?
        </p>
      </div>

      <div className="space-y-6">
        <div>
          <label
            htmlFor="address"
            className="block text-sm font-medium text-[#102018] mb-2"
          >
            Complete Address *
          </label>

          <textarea
            id="address"
            name="address"
            value={bookingData.address}
            onChange={handleInputChange}
            placeholder="Enter your complete address"
            rows="3"
            className="w-full px-4 py-3 border border-[#DDE8DF] rounded-xl focus:ring-2 focus:ring-[#70BD7D] focus:border-[#2F6F45] transition-all duration-200 resize-none bg-white text-[#102018]"
            required
          />
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <div>
            <label
              htmlFor="city"
              className="block text-sm font-medium text-[#102018] mb-2"
            >
              City *
            </label>

            <select
              id="city"
              name="city"
              value={bookingData.city}
              onChange={handleInputChange}
              className="w-full px-4 py-3 border border-[#DDE8DF] rounded-xl focus:ring-2 focus:ring-[#70BD7D] focus:border-[#2F6F45] transition-all duration-200 bg-white text-[#102018]"
              required
            >
              <option value="">Select City</option>
              <option value="Kalka">Kalka</option>
              <option value="Pinjore">Pinjore</option>
              <option value="Panchkula">Panchkula</option>
            </select>
          </div>

          <div>
            <label
              htmlFor="pincode"
              className="block text-sm font-medium text-[#102018] mb-2"
            >
              Pincode *
            </label>

            <input
              type="text"
              id="pincode"
              name="pincode"
              value={bookingData.pincode}
              onChange={handleInputChange}
              placeholder="Enter pincode"
              className="w-full px-4 py-3 border border-[#DDE8DF] rounded-xl focus:ring-2 focus:ring-[#70BD7D] focus:border-[#2F6F45] transition-all duration-200 bg-white text-[#102018]"
              required
            />
          </div>
        </div>

        <div>
          <label
            htmlFor="landmark"
            className="block text-sm font-medium text-[#102018] mb-2"
          >
            Landmark (Optional)
          </label>

          <input
            type="text"
            id="landmark"
            name="landmark"
            value={bookingData.landmark}
            onChange={handleInputChange}
            placeholder="Nearby landmark (e.g., Near Metro Station)"
            className="w-full px-4 py-3 border border-[#DDE8DF] rounded-xl focus:ring-2 focus:ring-[#70BD7D] focus:border-[#2F6F45] transition-all duration-200 bg-white text-[#102018]"
          />
        </div>
      </div>
    </motion.div>
  );

  const renderStep3 = () => (
    <motion.div
      initial={{ opacity: 0, x: 20 }}
      animate={{ opacity: 1, x: 0 }}
      exit={{ opacity: 0, x: -20 }}
      transition={{ duration: 0.3 }}
      className="space-y-6"
    >
      <div className="text-center mb-8">
        <div className="w-16 h-16 bg-gradient-to-r from-[#102018] to-[#2F6F45] rounded-2xl flex items-center justify-center mx-auto mb-4 shadow-lg">
          <Package className="w-8 h-8 text-white" />
        </div>

        <h2 className="text-2xl font-heading font-bold text-[#102018] mb-2">
          Scrap Details & Photos
        </h2>

        <p className="text-[#718078]">
          Tell us about your scrap items
        </p>
      </div>

      <div className="space-y-6">
        <div>
          <label
            htmlFor="scrapType"
            className="block text-sm font-medium text-[#102018] mb-2"
          >
            Type of Scrap *
          </label>

          <select
            id="scrapType"
            name="scrapType"
            value={bookingData.scrapType}
            onChange={handleInputChange}
            className="w-full px-4 py-3 border border-[#DDE8DF] rounded-xl focus:ring-2 focus:ring-[#70BD7D] focus:border-[#2F6F45] transition-all duration-200 bg-white text-[#102018]"
          >
            <option value="">Select scrap type</option>
            <option value="paper">📄 Paper & Cardboard</option>
            <option value="plastic">♻️ Plastic</option>
            <option value="metal">🔧 Metal (Iron, Steel, Aluminum)</option>
            <option value="ewaste">💻 E-Waste (Electronics)</option>
            <option value="glass">🍶 Glass</option>
            <option value="textile">👕 Textile & Clothes</option>
            <option value="mixed">📦 Mixed Scrap</option>
          </select>
        </div>

        <div>
          <label
            htmlFor="quantity"
            className="block text-sm font-medium text-[#102018] mb-2"
          >
            Estimated Quantity *
          </label>

          <select
            id="quantity"
            name="quantity"
            value={bookingData.quantity}
            onChange={handleInputChange}
            className="w-full px-4 py-3 border border-[#DDE8DF] rounded-xl focus:ring-2 focus:ring-[#70BD7D] focus:border-[#2F6F45] transition-all duration-200 bg-white text-[#102018]"
          >
            <option value="">Select quantity</option>
            <option value="small">Small (1-5 kg)</option>
            <option value="medium">Medium (5-20 kg)</option>
            <option value="large">Large (20-50 kg)</option>
            <option value="bulk">Bulk (50+ kg)</option>
          </select>
        </div>

        <div>
          <label
            htmlFor="photos"
            className="block text-sm font-medium text-[#102018] mb-2"
          >
            Upload Photos of Scrap Items
          </label>

          <div className="border-2 border-dashed border-[#DDE8DF] rounded-xl p-6 text-center hover:border-[#2F6F45] hover:bg-[#EAF5EC] transition-colors duration-200">
            <input
              type="file"
              id="photos"
              multiple
              accept="image/*"
              onChange={handlePhotoUpload}
              className="hidden"
            />

            <label htmlFor="photos" className="cursor-pointer">
              <Camera className="w-12 h-12 text-[#2F6F45] mx-auto mb-4" />

              <p className="text-[#718078] mb-2">
                Click to upload photos
              </p>

              <p className="text-sm text-[#718078]">
                PNG, JPG up to 10MB each
              </p>
            </label>
          </div>

          {bookingData.photos.length > 0 && (
            <div className="grid grid-cols-2 md:grid-cols-3 gap-4 mt-4">
              {bookingData.photos.map(photo => (
                <div key={photo.id} className="relative group">
                  <img
                    src={photo.preview}
                    alt="Scrap item"
                    className="w-full h-24 object-cover rounded-lg border border-[#DDE8DF]"
                  />

                  <button
                    type="button"
                    onClick={() => removePhoto(photo.id)}
                    className="absolute -top-2 -right-2 w-6 h-6 bg-[#102018] text-white rounded-full flex items-center justify-center text-sm hover:bg-[#2F6F45] transition-colors duration-200"
                  >
                    ×
                  </button>
                </div>
              ))}
            </div>
          )}
        </div>

        <div>
          <label
            htmlFor="additionalNotes"
            className="block text-sm font-medium text-[#102018] mb-2"
          >
            Additional Notes (Optional)
          </label>

          <textarea
            id="additionalNotes"
            name="additionalNotes"
            value={bookingData.additionalNotes}
            onChange={handleInputChange}
            placeholder="Any additional information about your scrap items"
            rows="3"
            className="w-full px-4 py-3 border border-[#DDE8DF] rounded-xl focus:ring-2 focus:ring-[#70BD7D] focus:border-[#2F6F45] transition-all duration-200 resize-none bg-white text-[#102018]"
          />
        </div>
      </div>
    </motion.div>
  );

  const renderStep4 = () => (
    <motion.div
      initial={{ opacity: 0, x: 20 }}
      animate={{ opacity: 1, x: 0 }}
      exit={{ opacity: 0, x: -20 }}
      transition={{ duration: 0.3 }}
      className="space-y-6"
    >
      <div className="text-center mb-8">
        <div className="w-16 h-16 bg-gradient-to-r from-[#2F6F45] to-[#70BD7D] rounded-2xl flex items-center justify-center mx-auto mb-4 shadow-lg">
          <Calendar className="w-8 h-8 text-white" />
        </div>

        <h2 className="text-2xl font-heading font-bold text-[#102018] mb-2">
          Schedule Pickup
        </h2>

        <p className="text-[#718078]">
          Choose your preferred date and time
        </p>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-8">
        <div>
          <label
            htmlFor="preferredDate"
            className="block text-sm font-medium text-[#102018] mb-2"
          >
            Preferred Date *
          </label>

          <input
            type="date"
            id="preferredDate"
            name="preferredDate"
            value={bookingData.preferredDate}
            onChange={handleInputChange}
            min={new Date().toISOString().split('T')[0]}
            className="w-full px-4 py-3 border border-[#DDE8DF] rounded-xl focus:ring-2 focus:ring-[#70BD7D] focus:border-[#2F6F45] transition-all duration-200 bg-white text-[#102018]"
          />
        </div>

        <div>
          <label
            htmlFor="preferredTime"
            className="block text-sm font-medium text-[#102018] mb-2"
          >
            Preferred Time *
          </label>

          <select
            id="preferredTime"
            name="preferredTime"
            value={bookingData.preferredTime}
            onChange={handleInputChange}
            className="w-full px-4 py-3 border border-[#DDE8DF] rounded-xl focus:ring-2 focus:ring-[#70BD7D] focus:border-[#2F6F45] transition-all duration-200 bg-white text-[#102018]"
          >
            <option value="">Select time slot</option>
            <option value="morning">🌅 Morning (9 AM - 12 PM)</option>
            <option value="afternoon">☀️ Afternoon (12 PM - 3 PM)</option>
            <option value="evening">🌆 Evening (3 PM - 6 PM)</option>
          </select>
        </div>
      </div>

      <div className="card bg-gradient-to-r from-[#EAF5EC] to-white border border-[#DDE8DF]">
        <div className="flex items-center mb-6">
          <div className="w-12 h-12 bg-[#EAF5EC] rounded-xl flex items-center justify-center mr-4 border border-[#DDE8DF]">
            <CheckCircle className="w-6 h-6 text-[#2F6F45]" />
          </div>

          <h3 className="text-xl font-heading font-semibold text-[#102018]">
            Booking Summary
          </h3>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div className="space-y-3">
            <div className="flex items-center space-x-3">
              <User className="w-4 h-4 text-[#2F6F45]" />
              <span className="text-sm text-[#718078]">Name:</span>
              <span className="text-sm font-medium text-[#102018]">
                {bookingData.name}
              </span>
            </div>

            <div className="flex items-center space-x-3">
              <Mail className="w-4 h-4 text-[#2F6F45]" />
              <span className="text-sm text-[#718078]">Email:</span>
              <span className="text-sm font-medium text-[#102018]">
                {bookingData.email}
              </span>
            </div>

            <div className="flex items-center space-x-3">
              <Phone className="w-4 h-4 text-[#2F6F45]" />
              <span className="text-sm text-[#718078]">Mobile:</span>
              <span className="text-sm font-medium text-[#102018]">
                {bookingData.mobile}
              </span>
            </div>

            <div className="flex items-start space-x-3">
              <MapPin className="w-4 h-4 text-[#2F6F45] mt-1" />
              <span className="text-sm text-[#718078]">Address:</span>
              <span className="text-sm font-medium text-[#102018]">
                {bookingData.address}, {bookingData.city} - {bookingData.pincode}
              </span>
            </div>
          </div>

          <div className="space-y-3">
            <div className="flex items-center space-x-3">
              <Package className="w-4 h-4 text-[#2F6F45]" />
              <span className="text-sm text-[#718078]">Scrap Type:</span>
              <span className="text-sm font-medium text-[#102018] capitalize">
                {bookingData.scrapType}
              </span>
            </div>

            <div className="flex items-center space-x-3">
              <FileText className="w-4 h-4 text-[#2F6F45]" />
              <span className="text-sm text-[#718078]">Quantity:</span>
              <span className="text-sm font-medium text-[#102018] capitalize">
                {bookingData.quantity}
              </span>
            </div>

            <div className="flex items-center space-x-3">
              <Camera className="w-4 h-4 text-[#2F6F45]" />
              <span className="text-sm text-[#718078]">Photos:</span>
              <span className="text-sm font-medium text-[#102018]">
                {bookingData.photos.length} uploaded
              </span>
            </div>

            <div className="flex items-center space-x-3">
              <Calendar className="w-4 h-4 text-[#2F6F45]" />
              <span className="text-sm text-[#718078]">Date:</span>
              <span className="text-sm font-medium text-[#102018]">
                {bookingData.preferredDate}
              </span>
            </div>

            <div className="flex items-center space-x-3">
              <Clock className="w-4 h-4 text-[#2F6F45]" />
              <span className="text-sm text-[#718078]">Time:</span>
              <span className="text-sm font-medium text-[#102018] capitalize">
                {bookingData.preferredTime}
              </span>
            </div>
          </div>
        </div>
      </div>
    </motion.div>
  );

  return (
    <div className="min-h-screen bg-gradient-to-br from-[#F6F8F4] to-[#EAF5EC]">
      <div className="container-custom py-8">

        {/* Header */}
        <div className="text-center mb-12">
          
          <h1 className="text-4xl md:text-5xl font-heading font-bold text-[#102018] mb-4">
               Book{' '}
              <span className="bg-gradient-to-r from-[#2F6F45] to-[#70BD7D] bg-clip-text text-transparent">
                Scrap Pickup
              </span>
            </h1>

          <p className="text-xl text-[#718078] max-w-2xl mx-auto">
            Complete your booking in 4 simple steps
          </p>
        </div>

        {/* Progress Bar */}
        <div className="max-w-4xl mx-auto mb-12">
          <div className="flex items-center justify-between">
            {[1, 2, 3, 4].map((step) => (
              <div key={step} className="flex items-center">
                <div
                  className={`w-12 h-12 rounded-full flex items-center justify-center text-sm font-semibold transition-all duration-300 ${
                    currentStep >= step
                      ? 'bg-gradient-to-r from-[#102018] to-[#2F6F45] text-white shadow-lg'
                      : 'bg-[#DDE8DF] text-[#718078]'
                  }`}
                >
                  {currentStep > step ? (
                    <CheckCircle className="w-6 h-6" />
                  ) : (
                    step
                  )}
                </div>

                {step < 4 && (
                  <div
                    className={`w-16 md:w-24 h-1 mx-2 transition-all duration-300 ${
                      currentStep > step
                        ? 'bg-gradient-to-r from-[#2F6F45] to-[#70BD7D]'
                        : 'bg-[#DDE8DF]'
                    }`}
                  />
                )}
              </div>
            ))}
          </div>

          <div className="flex justify-between mt-4 text-sm text-[#718078]">
            <span
              className={
                currentStep >= 1
                  ? 'text-[#2F6F45] font-medium'
                  : ''
              }
            >
              Personal Info
            </span>

            <span
              className={
                currentStep >= 2
                  ? 'text-[#2F6F45] font-medium'
                  : ''
              }
            >
              Address
            </span>

            <span
              className={
                currentStep >= 3
                  ? 'text-[#2F6F45] font-medium'
                  : ''
              }
            >
              Scrap Details
            </span>

            <span
              className={
                currentStep >= 4
                  ? 'text-[#2F6F45] font-medium'
                  : ''
              }
            >
              Schedule
            </span>
          </div>
        </div>

        {/* Form Content */}
        <div className="max-w-2xl mx-auto">
          <div className="card p-8 bg-white border border-[#DDE8DF] shadow-lg">
            <AnimatePresence mode="wait">
              {currentStep === 1 && renderStep1()}
              {currentStep === 2 && renderStep2()}
              {currentStep === 3 && renderStep3()}
              {currentStep === 4 && renderStep4()}
            </AnimatePresence>

            {/* Action Buttons */}
            <div className="flex justify-between mt-8 pt-6 border-t border-[#DDE8DF]">
              {currentStep > 1 && (
                <motion.button
                  type="button"
                  onClick={handlePrevious}
                  className="btn-outline flex items-center space-x-2 border-[#2F6F45] text-[#2F6F45] hover:bg-[#EAF5EC]"
                  whileHover={{ scale: 1.05 }}
                  whileTap={{ scale: 0.95 }}
                >
                  <ArrowLeft className="w-4 h-4" />
                  <span>Previous</span>
                </motion.button>
              )}

              <div className="flex-1" />

              {currentStep < 4 ? (
                <motion.button
                  type="button"
                  onClick={handleNext}
                  className="btn-primary flex items-center space-x-2 bg-[#2F6F45] hover:bg-[#102018]"
                  disabled={loading}
                  whileHover={{ scale: loading ? 1 : 1.05 }}
                  whileTap={{ scale: loading ? 1 : 0.95 }}
                >
                  <span>
                    {loading ? 'Processing...' : 'Next'}
                  </span>
                  <ArrowRight className="w-4 h-4" />
                </motion.button>
              ) : (
                <motion.button
                  type="button"
                  onClick={handleNext}
                  className="btn-primary flex items-center space-x-2 bg-[#2F6F45] hover:bg-[#102018]"
                  disabled={loading}
                  whileHover={{ scale: loading ? 1 : 1.05 }}
                  whileTap={{ scale: loading ? 1 : 0.95 }}
                >
                  <span>
                    {loading
                      ? 'Completing Booking...'
                      : 'Complete Booking'}
                  </span>

                  <CheckCircle className="w-4 h-4" />
                </motion.button>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default BookAppointment;